import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Keyboard,
  Maximize2,
  Minimize2,
  RefreshCw,
  X,
  Zap,
  Clipboard,
  ChevronDown,
} from "lucide-react";
import { ConsoleDiagnoseButton } from "@/components/console/ConsoleDiagnose";

/**
 * VM console.
 *
 * Opened in a separate tab by the VM detail page, which hits
 * ``GET /api/v1/vms/{id}/console`` first to obtain a one-shot ticket
 * and then navigates here with query params:
 *
 *   ``/console?path=/api/vnc/{id}?ticket=XXX&name=<vm>&pw=<pve>``
 *
 * From the user's point of view: a fullscreen dark canvas with a
 * floating toolbar on top. noVNC handles RFB + key mapping; we own
 * connection lifecycle (reconnect with backoff, status badge, close
 * codes → human messages).
 */

type ConnStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "disconnected";

const CLOSE_CODE_MESSAGES: Record<number, string> = {
  4001: "Ticket expired. Reopen the console from the VM page.",
  4004: "VM not found or not part of your tenant.",
  4401: "Proxmox refused the upgrade — the API token is missing VM.Console.",
  4408: "Console was idle for too long. Click Reconnect.",
  4500: "Unknown Proxmox node.",
  4502: "Backend could not reach Proxmox. Check the /console/diagnose endpoint.",
  1000: "Session closed.",
  1006: "Connection lost.",
};

// When the WS closes within this window, the browser never actually
// completed the TLS handshake — almost always a self-signed cert
// that the browser (especially Safari) refuses to trust for wss://.
const TLS_TRUST_SUSPICION_MS = 500;

function isLikelyTlsTrustIssue(connectedMs: number): boolean {
  return connectedMs < TLS_TRUST_SUSPICION_MS;
}

const KEYBOARD_SHORTCUTS: Array<{
  label: string;
  keys: number[];
  title?: string;
}> = [
  { label: "Ctrl+Alt+Del", keys: [0xffe3, 0xffe9, 0xffff] },
  { label: "Ctrl+Alt+F1", keys: [0xffe3, 0xffe9, 0xffbe] },
  { label: "Ctrl+Alt+F2", keys: [0xffe3, 0xffe9, 0xffbf] },
  { label: "Ctrl+Alt+F3", keys: [0xffe3, 0xffe9, 0xffc0] },
  { label: "Ctrl+Alt+F7", keys: [0xffe3, 0xffe9, 0xffc4] },
  { label: "Alt+Tab", keys: [0xffe9, 0xff09] },
  { label: "Esc", keys: [0xff1b] },
  { label: "Super", keys: [0xffeb], title: "Windows / GUI key" },
];

// noVNC RFB exposes these layouts at runtime via rfb.sendKey; the selector
// here is a hint to the operator and persists via localStorage. The value
// is also what we recommend setting inside the guest OS via ``loadkeys``
// (Linux) or the Settings keyboard panel (Windows) so host → VM maps cleanly.
type KbLayout = "fr" | "fr-mac" | "us" | "us-mac" | "de" | "gb";
const LAYOUT_LABELS: Record<KbLayout, string> = {
  fr: "FR (AZERTY, PC)",
  "fr-mac": "FR (AZERTY, Mac)",
  us: "US (QWERTY, PC)",
  "us-mac": "US (QWERTY, Mac)",
  de: "DE (QWERTZ)",
  gb: "GB (QWERTY)",
};

// Auto-reconnect schedule in seconds.
const RECONNECT_BACKOFF = [1, 3, 6];

export default function ConsolePage() {
  const params = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const wsPath = params.get("path");
  const vmName = params.get("name") ?? "VM";
  const vncPassword = params.get("pw") ?? "";

  // The WS path the VM detail page hands us always looks like
  // ``/api/vnc/{id}?ticket=...`` — extract the VM id so the diagnostic
  // overlay can hit ``/vms/{id}/console/diagnose`` when the WS closes
  // with anything other than a normal 1000. Null means "can't be sure",
  // in which case the diagnose button is simply not shown.
  const vmIdForDiagnose = useMemo<number | null>(() => {
    if (!wsPath) return null;
    const match = wsPath.match(/^\/api\/vnc\/(\d+)/);
    return match ? Number(match[1]) : null;
  }, [wsPath]);

  const screenRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<unknown>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  const [status, setStatus] = useState<ConnStatus>("idle");
  const [statusText, setStatusText] = useState("Preparing…");
  const [closeInfo, setCloseInfo] = useState<{ code: number; reason: string } | null>(null);
  const [tlsTrustSuspected, setTlsTrustSuspected] = useState(false);
  const openTimeRef = useRef(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [kbLayout, setKbLayout] = useState<KbLayout>(() => {
    const stored = localStorage.getItem("console.kblayout");
    return (stored as KbLayout) ?? "fr";
  });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // ── Connect / reconnect ──────────────────────────────────────────────

  const disconnectEverything = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (
      rfbRef.current &&
      typeof (rfbRef.current as { disconnect?: () => void }).disconnect === "function"
    ) {
      try {
        (rfbRef.current as { disconnect: () => void }).disconnect();
      } catch {
        /* ignore */
      }
    }
    rfbRef.current = null;
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(
    (attempt: number) => {
      if (!wsPath || !screenRef.current) {
        setStatus("error");
        setStatusText(!wsPath ? "Missing path parameter" : "Screen ref lost");
        return;
      }
      disconnectEverything();
      reconnectAttemptRef.current = attempt;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}${wsPath}`;

      setStatus(attempt === 0 ? "connecting" : "reconnecting");
      setStatusText(
        attempt === 0
          ? "Opening WebSocket…"
          : `Reconnecting (attempt ${attempt + 1}/${RECONNECT_BACKOFF.length})…`
      );
      setCloseInfo(null);

      openTimeRef.current = performance.now();
      const ws = new WebSocket(url, "binary");
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        setStatusText("Loading VNC…");
        import("@novnc/novnc/lib/rfb")
          .then(({ default: RFB }) => {
            if (!screenRef.current || wsRef.current !== ws) return;
            let rfb: InstanceType<typeof RFB>;
            try {
              rfb = new RFB(screenRef.current, ws);
            } catch (e) {
              setStatus("error");
              setStatusText(`RFB init error: ${e}`);
              return;
            }
            rfbRef.current = rfb;
            rfb.scaleViewport = true;
            rfb.resizeSession = true;
            rfb.qualityLevel = 6;
            rfb.compressionLevel = 2;
            rfb.showDotCursor = true;
            // Explicit: click anywhere on the canvas captures keyboard
            // focus. Default is true but we set it to be obvious.
            rfb.focusOnClick = true;
            rfb.viewOnly = false;

            rfb.addEventListener("connect", () => {
              setStatus("connected");
              setStatusText("Connected");
              reconnectAttemptRef.current = 0;
              document.title = `Console — ${vmName}`;
              // Auto-focus the hidden RFB input so the very first
              // keystroke reaches the guest without requiring a
              // click-to-focus dance.
              try {
                rfb.focus();
              } catch {
                // Older noVNC versions may not expose .focus(); the
                // click-to-focus path will still work.
              }
            });

            rfb.addEventListener("disconnect", (e: CustomEvent) => {
              if (e.detail.clean) {
                setStatus("disconnected");
                setStatusText("Session closed.");
              } else {
                // Non-clean disconnect → scheduleReconnect picks it up from ws.onclose.
              }
              rfbRef.current = null;
            });

            rfb.addEventListener("credentialsrequired", () => {
              if (vncPassword) {
                rfb.sendCredentials({ password: vncPassword });
              } else {
                const pw = window.prompt("VNC Password:");
                if (pw) rfb.sendCredentials({ password: pw });
              }
            });
          })
          .catch((err) => {
            setStatus("error");
            setStatusText(`Failed to load noVNC: ${err}`);
          });
      };

      ws.onerror = () => {
        // onerror fires before onclose; leave the close handler to do the work.
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;

        const elapsed = performance.now() - openTimeRef.current;

        // A sub-500ms 1006 close means the browser refused the TLS
        // handshake — self-signed cert trust, firewall, or similar.
        // This fires before the server has a chance to emit 4xxx.
        if (
          event.code === 1006 &&
          isLikelyTlsTrustIssue(elapsed) &&
          reconnectAttemptRef.current === 0
        ) {
          setTlsTrustSuspected(true);
        }

        const reason =
          CLOSE_CODE_MESSAGES[event.code] ??
          event.reason ??
          `WebSocket closed (${event.code})`;

        // 4xxx codes are explicit server-side rejections we should surface,
        // not auto-reconnect around (a fresh ticket is needed for 4001 etc).
        const permanent = event.code >= 4000 && event.code < 5000;
        setCloseInfo({ code: event.code, reason });

        if (permanent) {
          setStatus("error");
          setStatusText(reason);
          return;
        }

        // Unexpected close (e.g. 1006): try to reconnect unless we've
        // exhausted the backoff schedule.
        const nextAttempt = reconnectAttemptRef.current + 1;
        if (nextAttempt <= RECONNECT_BACKOFF.length) {
          const wait = RECONNECT_BACKOFF[nextAttempt - 1];
          setStatus("reconnecting");
          setStatusText(`${reason} — retrying in ${wait}s…`);
          reconnectTimerRef.current = window.setTimeout(
            () => connect(nextAttempt),
            wait * 1000
          );
        } else {
          setStatus("error");
          setStatusText(`${reason} Gave up after ${RECONNECT_BACKOFF.length} retries.`);
        }
      };
    },
    [wsPath, vmName, vncPassword, disconnectEverything]
  );

  useEffect(() => {
    connect(0);
    return () => {
      disconnectEverything();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────

  const sendShortcut = useCallback((keys: number[]) => {
    const rfb = rfbRef.current as
      | { sendKey: (keysym: number, code: string | null, down?: boolean) => void }
      | null;
    if (!rfb) return;
    // Press all keys down in order, then release in reverse — the canonical
    // way to emit a Ctrl+Alt+Del combo through noVNC.
    for (const k of keys) rfb.sendKey(k, null, true);
    for (const k of [...keys].reverse()) rfb.sendKey(k, null, false);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen().catch(() => undefined);
    } else {
      await document.exitFullscreen().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Map a printable character to an X11 keysym. For ASCII (including
  // every character a password typically uses) the keysym value IS the
  // ASCII code, which is what qemu's VNC server expects. For characters
  // outside the BMP (emoji, some special chars) we fall back to the
  // Unicode-plane-1 trick: 0x01000000 | codepoint.
  const keysymForChar = useCallback((ch: string): number => {
    const cp = ch.codePointAt(0);
    if (cp === undefined) return 0;
    if (cp === 0x0a || cp === 0x0d) return 0xff0d; // Enter → XK_Return
    if (cp === 0x09) return 0xff09; // Tab
    if (cp === 0x08) return 0xff08; // Backspace
    if (cp < 0x20 || cp === 0x7f) return 0; // skip other control codes
    if (cp < 0x100) return cp; // Latin-1
    return 0x01000000 | cp; // Unicode keysym
  }, []);

  const typeText = useCallback(
    async (text: string) => {
      const rfb = rfbRef.current as
        | { sendKey: (keysym: number, code: string | null, down?: boolean) => void }
        | null;
      if (!rfb) {
        alert(
          "Console pas encore connectee. Attends le badge vert puis re-essaye.",
        );
        return;
      }
      if (!text) return;
      // Small per-char delay so busy guests (serial TTY, slow kernel)
      // don't drop characters. 12ms works for Linux, Windows boot, and
      // even BIOS setup screens.
      for (const ch of text) {
        const k = keysymForChar(ch);
        if (!k) continue;
        rfb.sendKey(k, null, true);
        rfb.sendKey(k, null, false);
        await new Promise((r) => setTimeout(r, 12));
      }
    },
    [keysymForChar]
  );

  const typeFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) await typeText(text);
    } catch {
      alert(
        "Clipboard read was refused.\n" +
          "Use the 'Type text' field to paste manually."
      );
    }
  }, [typeText]);

  const typeCreds = useCallback(
    (which: "user" | "password") => {
      const val = which === "user"
        ? params.get("ci_user") ?? ""
        : params.get("ci_password") ?? "";
      if (!val) {
        alert(`No ${which} available for this VM.`);
        return;
      }
      void typeText(val);
    },
    [params, typeText]
  );

  // One-shot login helper: type user, Enter, wait, type password, Enter.
  // The waits are conservative — slow guests (boot still finishing,
  // serial TTY, BIOS pre-login) can drop chars otherwise.
  const performAutoLogin = useCallback(async () => {
    const user = params.get("ci_user") ?? "";
    const pwd = params.get("ci_password") ?? "";
    if (!user || !pwd) {
      alert(
        "Identifiants manquants pour cette VM (ci_user/ci_password absents).\n" +
          "Verifiez que la VM a ete provisionnee via AlphaProx (les VMs " +
          "importees a posteriori n'ont pas de credentials stockes).",
      );
      return;
    }
    const rfb = rfbRef.current as
      | { sendKey: (keysym: number, code: string | null, down?: boolean) => void }
      | null;
    if (!rfb) {
      alert("Console pas encore connectee — attendez le badge vert.");
      return;
    }
    await typeText(user);
    await new Promise((r) => setTimeout(r, 250));
    rfb.sendKey(0xff0d, null, true);
    rfb.sendKey(0xff0d, null, false);
    // Login prompt -> the pw prompt usually appears within 100-300ms;
    // 600ms is a safe upper bound that doesn't feel sluggish.
    await new Promise((r) => setTimeout(r, 600));
    await typeText(pwd);
    await new Promise((r) => setTimeout(r, 200));
    rfb.sendKey(0xff0d, null, true);
    rfb.sendKey(0xff0d, null, false);
  }, [params, typeText]);

  const handleLayoutChange = (layout: KbLayout) => {
    setKbLayout(layout);
    localStorage.setItem("console.kblayout", layout);
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (!wsPath) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-red-300">
        Missing console parameters.
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-gray-950 text-gray-100">
      <Toolbar
        vmName={vmName}
        status={status}
        statusText={statusText}
        isFullscreen={isFullscreen}
        kbLayout={kbLayout}
        hasCiUser={!!params.get("ci_user")}
        hasCiPassword={!!params.get("ci_password")}
        onLayoutChange={handleLayoutChange}
        onSendShortcut={sendShortcut}
        onToggleFullscreen={toggleFullscreen}
        onReconnect={() => connect(0)}
        onTypeFromClipboard={typeFromClipboard}
        onTypeText={typeText}
        onTypeCreds={typeCreds}
        onAutoLogin={performAutoLogin}
        shortcutsOpen={shortcutsOpen}
        setShortcutsOpen={setShortcutsOpen}
      />
      <div className="relative flex-1 overflow-hidden bg-black">
        <div
          ref={screenRef}
          className="h-full w-full"
          // Every pointer interaction re-focuses the hidden RFB
          // textarea, so the user never has to guess why their
          // keystrokes are going nowhere.
          onMouseDown={() => {
            const rfb = rfbRef.current as { focus?: () => void } | null;
            try {
              rfb?.focus?.();
            } catch {
              /* no-op */
            }
          }}
        />
        {status === "connected" && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 max-w-md rounded bg-gray-900/80 px-3 py-1.5 text-center text-xs text-gray-300 opacity-80">
            Clique dans la fenetre pour activer le clavier.
            {(() => {
              const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
              return isMac ? (
                <span className="ml-1 text-amber-300">
                  Mac : prefere le bouton <strong>Login auto</strong> — le
                  clavier physique macOS envoie souvent des chars qui ne
                  matchent pas la layout en-us du guest.
                </span>
              ) : null;
            })()}
          </div>
        )}
        {(status === "connecting" ||
          status === "reconnecting" ||
          status === "error") && (
          <Overlay
            status={status}
            statusText={statusText}
            closeInfo={closeInfo}
            tlsTrustSuspected={tlsTrustSuspected}
            vmIdForDiagnose={vmIdForDiagnose}
            onReconnect={() => {
              setTlsTrustSuspected(false);
              connect(0);
            }}
          />
        )}
      </div>
      <StatusBar
        vmName={vmName}
        status={status}
        kbLayout={kbLayout}
        closeInfo={closeInfo}
      />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function Toolbar(props: {
  vmName: string;
  status: ConnStatus;
  statusText: string;
  isFullscreen: boolean;
  kbLayout: KbLayout;
  hasCiUser: boolean;
  hasCiPassword: boolean;
  onAutoLogin: () => void;
  onLayoutChange: (l: KbLayout) => void;
  onSendShortcut: (keys: number[]) => void;
  onToggleFullscreen: () => void;
  onReconnect: () => void;
  onTypeFromClipboard: () => void;
  onTypeText: (text: string) => Promise<void>;
  onTypeCreds: (which: "user" | "password") => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (b: boolean) => void;
}) {
  const [typeOpen, setTypeOpen] = useState(false);
  const [manualText, setManualText] = useState("");
  const statusColors: Record<ConnStatus, string> = {
    idle: "bg-gray-600",
    connecting: "bg-blue-500 animate-pulse",
    reconnecting: "bg-amber-500 animate-pulse",
    connected: "bg-emerald-500",
    disconnected: "bg-gray-500",
    error: "bg-red-500",
  };
  const disabled = props.status !== "connected";
  return (
    <div className="flex h-10 shrink-0 items-center gap-3 border-b border-gray-800 bg-gray-900 px-3 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${statusColors[props.status]}`} />
        <span className="truncate font-semibold text-gray-100">{props.vmName}</span>
        <span className="text-xs text-gray-500">· {props.status}</span>
      </div>
      <div className="ml-auto flex items-center gap-1">
        {props.hasCiUser && props.hasCiPassword && (
          <button
            className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
            onClick={() => props.onAutoLogin()}
            disabled={disabled}
            title={
              "Tape login + Enter + password + Enter via le protocole VNC.\n" +
              "Recommande sur Mac : le clavier physique passe par la layout " +
              "macOS (AZERTY/QWERTZ) qui peut envoyer des chars differents " +
              "de ce que la VM attend (clavier=en-us cote QEMU). Le bouton " +
              "envoie les keysyms ASCII bruts → bypass complet de la layout."
            }
          >
            <Zap className="h-3 w-3" />
            Login auto
          </button>
        )}
        <button
          className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-50"
          onClick={() => props.onSendShortcut([0xffe3, 0xffe9, 0xffff])}
          disabled={disabled}
          title="Send Ctrl+Alt+Del"
        >
          <Zap className="mr-1 inline h-3 w-3" />
          Ctrl+Alt+Del
        </button>

        <div className="relative">
          <button
            className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
            onClick={() => props.setShortcutsOpen(!props.shortcutsOpen)}
            disabled={disabled}
            title="Keyboard shortcuts"
          >
            <Keyboard className="h-3 w-3" />
            <ChevronDown className="h-3 w-3" />
          </button>
          {props.shortcutsOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-gray-700 bg-gray-900 shadow-lg">
              {KEYBOARD_SHORTCUTS.map((s) => (
                <button
                  key={s.label}
                  className="block w-full px-3 py-1.5 text-left text-xs text-gray-200 hover:bg-gray-800"
                  onClick={() => {
                    props.onSendShortcut(s.keys);
                    props.setShortcutsOpen(false);
                  }}
                  title={s.title}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
            onClick={() => setTypeOpen(!typeOpen)}
            disabled={disabled}
            title="Type text into the VM (works at login prompts, BIOS, anywhere)"
          >
            <Clipboard className="h-3 w-3" />
            Type
            <ChevronDown className="h-3 w-3" />
          </button>
          {typeOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-72 rounded-md border border-gray-700 bg-gray-900 p-2 shadow-lg">
              <button
                className="mb-1 block w-full rounded px-2 py-1.5 text-left text-xs text-gray-200 hover:bg-gray-800"
                onClick={() => {
                  props.onTypeFromClipboard();
                  setTypeOpen(false);
                }}
              >
                Paste from system clipboard
              </button>
              {props.hasCiUser && (
                <button
                  className="mb-1 block w-full rounded px-2 py-1.5 text-left text-xs text-emerald-300 hover:bg-gray-800"
                  onClick={() => {
                    props.onTypeCreds("user");
                    setTypeOpen(false);
                  }}
                  title="Send the cloud-init username to the focused input"
                >
                  Type cloud-init user
                </button>
              )}
              {props.hasCiPassword && (
                <button
                  className="mb-1 block w-full rounded px-2 py-1.5 text-left text-xs text-emerald-300 hover:bg-gray-800"
                  onClick={() => {
                    props.onTypeCreds("password");
                    setTypeOpen(false);
                  }}
                  title="Send the cloud-init password to the focused input"
                >
                  Type cloud-init password
                </button>
              )}
              <div className="mt-2 border-t border-gray-800 pt-2">
                <label className="mb-1 block text-[10px] uppercase tracking-wide text-gray-500">
                  Type custom text
                </label>
                <textarea
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  rows={2}
                  className="w-full rounded border border-gray-700 bg-gray-800 px-2 py-1 font-mono text-xs text-gray-100"
                  placeholder="Paste anything (password, long command, ...)"
                />
                <button
                  disabled={!manualText}
                  onClick={async () => {
                    await props.onTypeText(manualText);
                    setManualText("");
                    setTypeOpen(false);
                  }}
                  className="mt-1 w-full rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  Send as keystrokes
                </button>
                <p className="mt-1 text-[10px] text-gray-500">
                  Characters are typed one by one (12 ms each). Works at the
                  login getty, BIOS, GRUB — anywhere the guest has a keyboard
                  focus. Password is not echoed back.
                </p>
              </div>
            </div>
          )}
        </div>

        <select
          value={props.kbLayout}
          onChange={(e) => props.onLayoutChange(e.target.value as KbLayout)}
          className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200"
          title="Keyboard layout hint (set the guest OS layout to match)"
        >
          {(Object.keys(LAYOUT_LABELS) as KbLayout[]).map((l) => (
            <option key={l} value={l}>
              {LAYOUT_LABELS[l]}
            </option>
          ))}
        </select>

        <button
          className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700"
          onClick={props.onReconnect}
          title="Reconnect"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
        <button
          className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700"
          onClick={props.onToggleFullscreen}
          title={props.isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {props.isFullscreen ? (
            <Minimize2 className="h-3 w-3" />
          ) : (
            <Maximize2 className="h-3 w-3" />
          )}
        </button>
        <button
          className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
          onClick={() => window.close()}
          title="Close console"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function StatusBar(props: {
  vmName: string;
  status: ConnStatus;
  kbLayout: KbLayout;
  closeInfo: { code: number; reason: string } | null;
}) {
  return (
    <div className="flex h-6 shrink-0 items-center gap-4 border-t border-gray-800 bg-gray-900 px-3 text-[10px] text-gray-500">
      <span>{LAYOUT_LABELS[props.kbLayout]}</span>
      <span>Press Ctrl+Alt+Shift to release the mouse</span>
      {props.closeInfo && (
        <span className="ml-auto text-amber-400">
          last close: {props.closeInfo.code}
        </span>
      )}
    </div>
  );
}

function Overlay(props: {
  status: ConnStatus;
  statusText: string;
  closeInfo: { code: number; reason: string } | null;
  tlsTrustSuspected: boolean;
  vmIdForDiagnose: number | null;
  onReconnect: () => void;
}) {
  const isError = props.status === "error";
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="pointer-events-auto max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-700 bg-gray-900/95 px-6 py-5 shadow-2xl backdrop-blur">
        <div className="mb-2 flex items-center gap-3">
          <div
            className={`h-2.5 w-2.5 rounded-full ${
              isError
                ? "bg-red-500"
                : props.status === "reconnecting"
                ? "bg-amber-500 animate-pulse"
                : "bg-blue-500 animate-pulse"
            }`}
          />
          <span className={`text-sm font-medium ${isError ? "text-red-300" : "text-gray-200"}`}>
            {isError ? "Console unavailable" : props.statusText}
          </span>
        </div>
        {isError && (
          <>
            <p className="max-w-sm text-xs text-gray-400">{props.statusText}</p>
            {props.tlsTrustSuspected && <TlsTrustHelp />}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={props.onReconnect}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500"
              >
                <RefreshCw className="mr-1 inline h-3 w-3" />
                Reconnect
              </button>
              {props.vmIdForDiagnose !== null && (
                <ConsoleDiagnoseButton vmId={props.vmIdForDiagnose} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function TlsTrustHelp() {
  const host = window.location.host; // e.g. 192.168.50.234:8880
  const installCmd = `openssl s_client -connect ${host} -showcerts </dev/null 2>/dev/null \\
  | openssl x509 > /tmp/alphaprox.crt
sudo security add-trusted-cert -d -r trustRoot \\
  -k /Library/Keychains/System.keychain /tmp/alphaprox.crt
# Then quit Safari completely (Cmd+Q) and retry.`;
  return (
    <div className="mt-4 rounded border border-amber-700/50 bg-amber-950/40 p-3 text-xs text-amber-100">
      <div className="mb-1 font-semibold">Likely: self-signed TLS certificate</div>
      <p className="mb-2 text-amber-200/90">
        The WebSocket closed before the handshake finished. Chrome and Firefox
        share TLS trust with the page you already accepted; Safari doesn't —
        it checks WSS certificates separately and refuses self-signed ones
        silently.
      </p>
      <p className="mb-1 font-semibold">On macOS, install the cert system-wide:</p>
      <pre className="overflow-x-auto whitespace-pre rounded bg-black/60 p-2 font-mono text-[10px] leading-tight text-amber-100">
        {installCmd}
      </pre>
      <p className="mt-2 text-amber-200/80">
        Alternative: open the console in Chrome or Firefox, where the
        cert you already accepted for the page works for WSS as well.
      </p>
    </div>
  );
}
